import { useMemo } from "react";
import { useUserQuery } from "@/api/v1/user/user-api";
import {
  getPlanLimits,
  type PlanLimits,
  type Feature,
  hasFeatureAccess,
  getLimit,
  isUnlimited,
} from "@/api/v1/payment/subscription-types";

/**
 * Hook to get the current user's subscription plan limits
 */
export function useSubscriptionLimits() {
  const { data: user, isLoading } = useUserQuery();

  // Check if subscription is active
  const activeStatuses = ["active", "trialing", "past_due", "incomplete"];
  const isActive = user?.subscription_status
    ? activeStatuses.includes(user.subscription_status)
    : false;

  // Determine effective plan: only use pro limits if subscription is active
  // Otherwise, treat as free plan
  const effectivePlanSlug = useMemo(() => {
    if (user?.subscription_plan_slug === "pro" && isActive) {
      return "pro";
    }
    return "free";
  }, [user?.subscription_plan_slug, isActive]);

  const limits: PlanLimits = useMemo(() => {
    return getPlanLimits(effectivePlanSlug);
  }, [effectivePlanSlug]);

  return {
    limits,
    planSlug: effectivePlanSlug,
    isActive,
    isLoading,
  };
}

/**
 * Hook to check if user has access to a specific feature
 * @param feature - The feature to check
 * @param currentCount - Optional current count of the feature (e.g., current number of geofences)
 */
export function useFeatureAccess(
  feature: Feature,
  currentCount?: number
) {
  const { limits, isLoading } = useSubscriptionLimits();

  const hasAccess = useMemo(() => {
    return hasFeatureAccess(limits, feature, currentCount);
  }, [limits, feature, currentCount]);

  const limit = useMemo(() => {
    return getLimit(limits, feature);
  }, [limits, feature]);

  const isUnlimitedFeature = useMemo(() => {
    return isUnlimited(limit);
  }, [limit]);

  const remaining = useMemo(() => {
    if (isUnlimitedFeature || currentCount === undefined) {
      return null; // null means unlimited or unknown
    }
    return Math.max(0, limit - currentCount);
  }, [limit, currentCount, isUnlimitedFeature]);

  return {
    hasAccess,
    limit,
    isUnlimited: isUnlimitedFeature,
    remaining,
    isLoading,
  };
}
